import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import { getProfilePictureSrc } from '../helper';

const profilePictureMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const maxSelectedProfilePictureBytes = 8 * 1024 * 1024;
const maxUploadProfilePictureBytes = 2 * 1024 * 1024;
const maxProfilePictureDimension = 800;
const profilePictureUploadType = 'image/jpeg';
const profilePictureUploadQuality = 0.86;

function loadProfilePictureImage(file) {
    return new Promise((resolve, reject) => {
        const imageUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(imageUrl);
            resolve(image);
        };

        image.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            reject(new Error('Unable to read image.'));
        };

        image.src = imageUrl;
    });
}

function canvasToBlob(canvas, type, quality) {
    return new Promise(resolve => {
        canvas.toBlob(resolve, type, quality);
    });
}

async function prepareProfilePictureUpload(file) {
    const image = await loadProfilePictureImage(file);
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;

    if (!naturalWidth || !naturalHeight) {
        throw new Error('Unable to read image dimensions.');
    }

    const scale = Math.min(1, maxProfilePictureDimension / Math.max(naturalWidth, naturalHeight));
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Unable to prepare image.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    for (const quality of [profilePictureUploadQuality, 0.76, 0.66]) {
        const blob = await canvasToBlob(canvas, profilePictureUploadType, quality);

        if (blob && blob.size <= maxUploadProfilePictureBytes) {
            return blob;
        }
    }

    throw new Error('Prepared image is too large.');
}
function Profile() {
    const { state } = useContext(AuthContext);
    const [millixAddress, setMillixAddress] = useState('');

    const [user, setUser] = useState({});
    const profilePictureInputRef = useRef(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    const [profilePictureUploading, setProfilePictureUploading] = useState(false);
    const [profilePictureError, setProfilePictureError] = useState('');
    const [profilePictureMessage, setProfilePictureMessage] = useState('');

    const [firstname, setFirstname] = useState('');
    const [showEditFname, setShowEditFname] = useState(false);

    const [lastname, setLastname] = useState('');
    const [showEditLname, setShowEditLname] = useState(false);

    const [phone, setPhone] = useState('');
    const [showEditPhone, setShowEditPhone] = useState(false);

    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showEditPwd, setShowEditPwd] = useState(false);

    const [pwdError, setPwdError] = useState('');

    useEffect(() => {
        axios.get(
            `${process.env.REACT_APP_SERVER}/api/millix/address`
        ).then(res => {
            if (res.status !== 200) {
                setMillixAddress("Invalid address");
                return;
            }
            setMillixAddress(res.data.address);
        });

        if (state.id) {
            axios.post(
                `${process.env.REACT_APP_SERVER}/api/user/get/${state.id}`
            ).then(
                res => {
                    if (res.status !== 200) {
                        return;
                    }
                    const nextUser = res.data;
                    setUser(nextUser);
                    setFirstname(nextUser.first_name);
                    setLastname(nextUser.last_name);
                    setPhone(nextUser.phone_number);
                    setProfilePictureUrl(nextUser.profile_picture_url || null);
                }
            );
        }
    }, [state]);

    const handleCopy = (e) => {
        navigator.clipboard.writeText(millixAddress);
    };

    function openProfilePicturePicker(e) {
        e.preventDefault();

        if (!profilePictureUploading) {
            profilePictureInputRef.current?.click();
        }
    }

    async function handleProfilePictureChange(e) {
        const [file] = e.target.files || [];

        if (!file) {
            return;
        }

        setProfilePictureError('');
        setProfilePictureMessage('');

        if (!profilePictureMimeTypes.includes(file.type)) {
            setProfilePictureError('Please choose a JPEG, PNG, WebP, or GIF image.');
            e.target.value = '';
            return;
        }

        if (file.size > maxSelectedProfilePictureBytes) {
            setProfilePictureError('Please choose an image smaller than 8 MB.');
            e.target.value = '';
            return;
        }

        setProfilePictureUploading(true);

        try {
            const uploadBlob = await prepareProfilePictureUpload(file);
            const res = await axios.post(
                `${process.env.REACT_APP_SERVER}/api/user/profile-picture/${state.id}`,
                uploadBlob,
                { headers: { 'Content-Type': uploadBlob.type || profilePictureUploadType } }
            );

            const uploadedUrl = res.data.profile_picture_url || null;
            setProfilePictureUrl(uploadedUrl);
            setUser(prev => ({ ...prev, profile_picture_url: uploadedUrl }));
            window.dispatchEvent(new CustomEvent('profile-picture-updated', { detail: uploadedUrl }));
            setProfilePictureMessage('Profile picture updated.');
        } catch (error) {
            const responseError = error.response?.data?.error;
            setProfilePictureError(responseError || error.message || 'Unable to upload profile picture.');
        } finally {
            setProfilePictureUploading(false);
            e.target.value = '';
        }
    }
    if (state.loading) {
        return <div>Loading...</div>
    }

    // Firstname
    function showEditFirstName(e) {
        e.preventDefault();
        setShowEditFname(true);
    }

    async function saveFirstName() {
        await axios.post(
            `${process.env.REACT_APP_SERVER}/api/user/update/${state.id}`, { first_name: firstname }
        ).then(
            res => {
                if (res.status !== 200) {
                    return;
                }
                console.log(res.data);
            }
        );
        setUser(prev => ({ ...prev, first_name: firstname }));
        setShowEditFname(false);
    }

    function cancelFirstName() {
        setShowEditFname(false);
    }

    // Lastname
    function showEditLastName(e) {
        e.preventDefault();
        setShowEditLname(true);
    }

    async function saveLastName() {
        await axios.post(
            `${process.env.REACT_APP_SERVER}/api/user/update/${state.id}`, { last_name: lastname }
        ).then(
            res => {
                if (res.status !== 200) {
                    return;
                }
                console.log(res.data);
            }
        );
        setUser(prev => ({ ...prev, last_name: lastname }));
        setShowEditLname(false);
    }

    function cancelLastName() {
        setShowEditLname(false);
    }

    // Phone
    function showEditPhoneNum(e) {
        e.preventDefault();
        setShowEditPhone(true);
    }

    async function savePhoneNum() {
        await axios.post(
            `${process.env.REACT_APP_SERVER}/api/user/update/${state.id}`, { phone_number: phone }
        ).then(
            res => {
                if (res.status !== 200) {
                    return;
                }
                console.log(res.data);
            }
        );
        setUser(prev => ({ ...prev, phone_number: phone }));
        setShowEditPhone(false);
    }

    function cancelPhoneNum() {
        setShowEditPhone(false);
    }

    // Password
    function showEditPWD(e) {
        e.preventDefault();
        setShowEditPwd(true);
    }

    async function savePWD() {
        if (newPwd !== confirmPwd) {
            setPwdError("Passwords do not match.");
            return;
        }

        setPwdError(''); // Clear error before saving

        await axios.post(
            `${process.env.REACT_APP_SERVER}/api/user/update/${state.id}`, { password: newPwd }
        ).then(
            res => {
                if (res.status !== 200) {
                    return;
                }
                console.log(res.data);
            }
        );
        setUser(prev => ({ ...prev, password: newPwd }));
        setShowEditPwd(false);
    }

    function cancelPWD() {
        setShowEditPwd(false);
        setPwdError('');
        setNewPwd('');
        setConfirmPwd('');
    }

    return (
        <>
            {!state.isAuthenticated && <Navigate replace to='/login' />}

            <Navigation />

            <div id="appCapsule">
                <div className="section mt-4 text-center">
                    <h1>Profile</h1>
                    <h4>{user.username && <strong>{`${user.username}`}</strong>}</h4>
                </div>

                <div className="section mt-3 text-center">
                    <div className="avatar-section">
                        <button
                            type="button"
                            className="avatar-upload-button"
                            onClick={openProfilePicturePicker}
                            disabled={profilePictureUploading}
                            aria-label="Upload profile picture"
                        >
                            <img
                                src={getProfilePictureSrc(user.profile_picture_url || profilePictureUrl)}
                                alt={`${user.username || 'Profile'} avatar`}
                                className="imaged w100 rounded profile-avatar-image"
                            />
                            <span className="button">
                                <ion-icon name="camera-outline" role="img" className="md hydrated" aria-label="camera outline"></ion-icon>
                            </span>
                        </button>
                        <input
                            ref={profilePictureInputRef}
                            type="file"
                            className="pagado-hide"
                            accept={profilePictureMimeTypes.join(',')}
                            onChange={handleProfilePictureChange}
                        />
                    </div>
                    {profilePictureUploading && <div className="profile-picture-status text-muted mt-2">Uploading...</div>}
                    {profilePictureError && <div className="profile-picture-status text-danger mt-2">{profilePictureError}</div>}
                    {profilePictureMessage && !profilePictureError && <div className="profile-picture-status text-success mt-2">{profilePictureMessage}</div>}
                </div>

                <div className='section mt-4 text-center'>
                    <span className="text-success">
                        <strong className='me-1'>Millix address:</strong>
                    </span>
                    {millixAddress}
                    <FontAwesomeIcon className='copy-action ms-1' onClick={handleCopy} icon={faCopy} />
                </div>

                <div className='listview-title mt-1'>Profile Settings</div>
                <ul class="listview image-listview text inset">
                    <li>
                        <div class="item">
                            <div class="in">
                                <div>First name</div>
                                <span class="text-muted">{user.first_name && <strong>{`${user.first_name}`}</strong>}</span>
                                <a href='#' id='show-edit-firstname' onClick={(e) => showEditFirstName(e)}>Edit</a>
                            </div>
                        </div>
                    </li>
                    {showEditFname && <li>
                        <div className='item'>
                            <form className="edit-form">
                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" for="first_name">Edit first name</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="first_name"
                                            placeholder="Enter first name"
                                            value={firstname}
                                            onChange={(e) => setFirstname(e.target.value)}
                                        />
                                        <i class="clear-input">
                                            <ion-icon name="close-circle"></ion-icon>
                                        </i>
                                    </div>
                                </div>

                                <div className='edit-action'>
                                    <button type="button" onClick={saveFirstName} className="btn btn-success btn-block">Save</button>
                                    <button type="button" onClick={cancelFirstName} className="btn btn-primary btn-block">Cancel</button>
                                </div>
                            </form>
                        </div>
                    </li>}
                    <li>
                        <div className="item">
                            <div class="in">
                                <div>Last name</div>
                                <span className="text-muted">{user.last_name && <strong>{`${user.last_name}`}</strong>}</span>
                                <a href='#' id='show-edit-lastname' onClick={(e) => showEditLastName(e)}>Edit</a>
                            </div>
                        </div>
                    </li>
                    {showEditLname && <li>
                        <div className='item'>
                            <form className="edit-form">
                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" for="last_name">Edit last name</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="last_name"
                                            placeholder="Enter last name"
                                            value={lastname}
                                            onChange={(e) => setLastname(e.target.value)}
                                        />
                                        <i class="clear-input">
                                            <ion-icon name="close-circle"></ion-icon>
                                        </i>
                                    </div>
                                </div>

                                <div className='edit-action'>
                                    <button type="button" onClick={saveLastName} className="btn btn-success btn-block">Save</button>
                                    <button type="button" onClick={cancelLastName} className="btn btn-primary btn-block">Cancel</button>
                                </div>
                            </form>
                        </div>
                    </li>}

                    <li>
                        <div href="#" className="item">
                            <div className="in">
                                <div>Phone</div>
                                <span className="text-muted">{user.phone_number && <strong>{`${user.phone_number}`}</strong>}</span>
                                <a href='#' id='show-edit-phone' onClick={(e) => showEditPhoneNum(e)}>Edit</a>
                            </div>
                        </div>
                    </li>
                    {showEditPhone && <li>
                        <div className='item'>
                            <form className="edit-form">
                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" for="phone">Edit phone number</label>
                                        <input
                                            type="tel"
                                            class="form-control"
                                            id="phone"
                                            placeholder="Enter last name"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                        />
                                        <i class="clear-input">
                                            <ion-icon name="close-circle"></ion-icon>
                                        </i>
                                    </div>
                                </div>

                                <div className='edit-action'>
                                    <button type="button" onClick={savePhoneNum} className="btn btn-success btn-block">Save</button>
                                    <button type="button" onClick={cancelPhoneNum} className="btn btn-primary btn-block">Cancel</button>
                                </div>
                            </form>
                        </div>
                    </li>}
                </ul>

                <div className='listview-title mt-1'>Security</div>
                <ul className="listview image-listview text inset">
                    <li>
                        <div className="item">
                            <div className="in">
                                <div>Update password</div>
                                <a href='#' id='show-edit-pwd' onClick={(e) => showEditPWD(e)}>Edit</a>
                            </div>
                        </div>
                    </li>
                    {showEditPwd && <li>
                        <div className='item'>
                            <form className="edit-form">
                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" for="new_pwd">New password</label>
                                        <input
                                            type="password"
                                            class="form-control"
                                            id="new_pwd"
                                            placeholder="Enter new password"
                                            onChange={(e) => setNewPwd(e.target.value)}
                                        />
                                        <i class="clear-input">
                                            <ion-icon name="close-circle"></ion-icon>
                                        </i>
                                    </div>
                                </div>

                                <div className="form-group basic">
                                    <div className="input-wrapper">
                                        <label className="label" for="confirm_pwd">Confirm password</label>
                                        <input
                                            type="password"
                                            class="form-control"
                                            id="confirm_pwd"
                                            placeholder="Confirm new password"
                                            onChange={(e) => setConfirmPwd(e.target.value)}
                                        />
                                        <i class="clear-input">
                                            <ion-icon name="close-circle"></ion-icon>
                                        </i>
                                    </div>
                                </div>

                                {pwdError && (
                                    <div className="form-group basic">
                                        <div className="input-wrapper">
                                            <small className="text-danger">{pwdError}</small>
                                        </div>
                                    </div>
                                )}

                                <div className='edit-action'>
                                    <button type="button" onClick={savePWD} className="btn btn-success btn-block">Save</button>
                                    <button type="button" onClick={cancelPWD} className="btn btn-primary btn-block">Cancel</button>
                                </div>
                            </form>
                        </div>
                    </li>}
                </ul>
            </div >
            <Footer />
        </>
    );
}

export default Profile;
